import { useMutation } from '@tanstack/react-query';
import useGetSubscription from './useGetSubscription';
import { createPortalSession } from '../../services/subscriptions';
import { CreatePortalSessionType } from '../../types/subscriptions';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';

interface SubscriptionPortalOptions {
  autoOpen?: boolean;
  onSuccess?: (response: string, type?: CreatePortalSessionType) => void;
  onError?: () => void | Promise<void>;
}

// Set right before redirecting to the Stripe payment portal so that, on
// return, a canceled subscription can prompt the user to subscribe rather than
// leaving them on a dead-end after adding a payment method.
export const PROMPT_SUBSCRIBE_AFTER_PAYMENT_KEY =
  'porter:promptSubscribeAfterPayment';

function useSubscriptionPortal({
  autoOpen = true,
  onSuccess,
  onError,
}: SubscriptionPortalOptions = {}) {
  const { user } = useUser<LoadedUser>();
  const { data, isFetching } = useGetSubscription();

  const businessId = user?.business?.id;

  const launchPortalMutation = useMutation({
    mutationFn: async (type?: CreatePortalSessionType) => {
      if (!data?.subscription || isFetching || !businessId) return null;

      const response = await createPortalSession(
        data.subscription.id,
        businessId,
        type,
      );

      return response?.url;
    },
    onSuccess: (url, type) => {
      if (!url) return;

      if (onSuccess) {
        onSuccess(url, type);
      }

      if (autoOpen) {
        // A payment-method portal session means the user is adding/updating a
        // card; flag it so we can prompt a canceled subscription to subscribe
        // on return instead of dead-ending after the card is saved.
        if (type === 'payment_method_update' || !type) {
          sessionStorage.setItem(PROMPT_SUBSCRIBE_AFTER_PAYMENT_KEY, 'true');
        }
        window.location.href = url;
      }
    },
    onError,
  });

  return {
    launchPortalMutation,
    isPendingCancel:
      launchPortalMutation.isPending &&
      launchPortalMutation.variables === 'subscription_cancel',
    isPendingUpdatePayment:
      launchPortalMutation.isPending &&
      (launchPortalMutation.variables === 'payment_method_update' ||
        !launchPortalMutation.variables),
    isSuccessCancel:
      launchPortalMutation.isSuccess &&
      launchPortalMutation.variables === 'subscription_cancel',
    isSuccessUpdatePayment:
      launchPortalMutation.isSuccess &&
      (launchPortalMutation.variables === 'payment_method_update' ||
        !launchPortalMutation.variables),
  };
}

export default useSubscriptionPortal;
